import React, { useEffect, useState, useCallback } from "react";
import {
    Box, Typography, Grid, TextField, Autocomplete, FormControl,
    InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar
} from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { postData, getData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";

// Maps backend (snake_case) field names to this form's formData/formErrors keys
const BACKEND_FIELD_MAP = {
    first_name: "firstName",
    last_name: "lastName",
    email: "email",
    phone_number: "phoneNumber",
    dob: "dateOfBirth",
    doj: "joiningDate",
    department: "department",
    team: "team",
    designation: "designation",
    role_id: "roleId",
    report_manager_id: "reportingManager",
    location_id: "location",
    emp_id: "employeeId",
};

function AddEmployee({ onClose, refreshData }) {
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const orgId = userData?.organization_id;

    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [teams, setTeams] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [roles, setRoles] = useState([]);
    const [teamLeadsAndManagers, setTeamLeadsAndManagers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loadingDepts, setLoadingDepts] = useState(false);
    const [loadingTeams, setLoadingTeams] = useState(false);
    const [loadingDesignations, setLoadingDesignations] = useState(false);
    const [loadingRoles, setLoadingRoles] = useState(false);
    const [loadingTeamLeadsAndManagers, setLoadingTeamLeadsAndManagers] = useState(false);
    const [loadingLocations, setLoadingLocations] = useState(false);
    const [existingEmployeeIds, setExistingEmployeeIds] = useState([]);
    const [isIdAvailable, setIsIdAvailable] = useState(null);

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        dateOfBirth: null,
        joiningDate: null,
        department: "",
        departmentName: "",
        team: "",
        teamName: "",
        designation: "",
        designationName: "",
        roleId: "",
        reportingManager: "",
        status: "inActive",
        employeeId: "",
        location: "",
    });

    const [formErrors, setFormErrors] = useState({});
    const [addEmployeeErrorMessage, setAddEmployeeErrorMessage] = useState("");
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // --- Helper to clear errors on change ---
    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // If an error exists for this field, remove it
        if (formErrors[field]) {
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    // --- API Fetchers ---
    const fetchDepartments = useCallback(async () => {
        if (!orgId) return;
        setLoadingDepts(true);
        try {
            const res = await getData(`${API_ENDPOINTS.fetchDepartmentsByOrganizationId}/${orgId}`);
            if (res?.status === "SUCCESS") {
                setDepartments(res.message.departments || []);
            }
        } catch (error) {
            return null;
        } finally {
            setLoadingDepts(false);
        }
    }, [orgId]);

    const fetchTeamsByDepartmentId = useCallback(async (deptId) => {
        setLoadingTeams(true);
        try {
            const res = await getData(`${API_ENDPOINTS.fetchTeamsByDepartmentId}/${deptId}`);
            if (res?.status === "SUCCESS") {
                setTeams(res.message.teams || []);
            } else {
                setTeams([]);
            }
        } catch (error) {
            setTeams([]);
        } finally {
            setLoadingTeams(false);
        }
    }, []);

    const fetchDesignationsByTeamId = useCallback(async (teamId) => {
        setLoadingDesignations(true);
        try {
            // FIX: Ensure this is called as a function
            const url = typeof API_ENDPOINTS.fetchDesignationsByTeamId === 'function'
                ? API_ENDPOINTS.fetchDesignationsByTeamId(teamId)
                : `${API_ENDPOINTS.fetchDesignationsByTeamId}/${teamId}`;

            const res = await getData(url);
            if (res?.status === "SUCCESS") {
                setDesignations(res.message.designations || []);
            }
        } catch (error) {
            setDesignations([]);
        } finally {
            setLoadingDesignations(false);
        }
    }, []);

    const getOrganizationRoles = useCallback(async () => {
        if (!orgId) return;
        setLoadingRoles(true);
        try {
            const url = typeof API_ENDPOINTS.fetchRolesByOrganizationId === 'function'
                ? API_ENDPOINTS.fetchRolesByOrganizationId(orgId)
                : `${API_ENDPOINTS.fetchRolesByOrganizationId}/${orgId}`;

            const res = await getData(url);
            if (res?.status === "SUCCESS") {
                setRoles(res.result || []);
            } else {
                setRoles([]);
            }
        } catch (error) {
            setRoles([]);
        } finally {
            setLoadingRoles(false);
        }
    }, [orgId]);

    const fetchTeamLeadsAndManagers = useCallback(async () => {
        if (!orgId) return;
        setLoadingTeamLeadsAndManagers(true);
        try {
            const url = typeof API_ENDPOINTS.getTeamLeadsAndManagers === 'function'
                ? API_ENDPOINTS.getTeamLeadsAndManagers(orgId)
                : `${API_ENDPOINTS.getTeamLeadsAndManagers}/${orgId}`;

            const res = await getData(url);
            if (res?.status === "SUCCESS") {
                const allUsers = res.message?.users || [];

                // 1. Filter: Only keep Manager and Team Lead user_types
                const filtered = allUsers.filter(user =>
                    user.user_type === "Manager" || user.user_type === "Team Lead"
                );

                // 2. Sort: Managers first, then Team Leads
                const sorted = filtered.sort((a, b) => {
                    if (a.user_type === "Manager" && b.user_type !== "Manager") return -1;
                    if (a.user_type !== "Manager" && b.user_type === "Manager") return 1;
                    return 0;
                });

                setTeamLeadsAndManagers(sorted);
            } else {
                setTeamLeadsAndManagers([]);
            }
        } catch (error) {
            setTeamLeadsAndManagers([]);
        } finally {
            setLoadingTeamLeadsAndManagers(false);
        }
    }, [orgId]);

    const getOrganizationLocations = useCallback(async () => {
        if (!orgId) return;
        setLoadingLocations(true);
        try {

            const url = typeof API_ENDPOINTS.fetchLocationsByOrganizationId === 'function'
                ? API_ENDPOINTS.fetchLocationsByOrganizationId(orgId)
                : `${API_ENDPOINTS.fetchLocationsByOrganizationId}/${orgId}`;

            const res = await getData(url);
            if (res?.status === "SUCCESS") {
                setLocations(res.message || []);
            }
        } catch (error) {
            return null;
        } finally {
            setLoadingLocations(false);
        }
    }, [orgId]);

    const fetchExistingEmployees = useCallback(async () => {
        if (!orgId) return;
        try {
            const res = await getData(API_ENDPOINTS.fetchEmployeesByOrganizationId(orgId));
            if (res?.status === "SUCCESS") {
                const ids = res.message.employees.map(emp => emp.emp_id.toLowerCase());
                setExistingEmployeeIds(ids);
            }
        } catch (error) {
           return null;
        }
    }, [orgId]);

    useEffect(() => {
        fetchDepartments();
        getOrganizationRoles();
        fetchTeamLeadsAndManagers();
        getOrganizationLocations();
        fetchExistingEmployees();
    }, [fetchDepartments, getOrganizationRoles, fetchTeamLeadsAndManagers, getOrganizationLocations, fetchExistingEmployees]);

    // --- Change Handlers ---
    const handleDepartmentChange = (event, val) => {
        setFormData(prev => ({
            ...prev,
            department: val?.id || "",
            departmentName: val?.department || "",
            team: "",
            teamName: "",
            designation: "",
            designationName: ""
        }));

        // Clear department error specifically
        if (formErrors.department) {
            setFormErrors(prev => ({ ...prev, department: undefined }));
        }

        setTeams([]);
        setDesignations([]);
        if (val?.id) fetchTeamsByDepartmentId(val.id);
    };

    const handleTeamChange = (event, val) => {
        setFormData(prev => ({
            ...prev,
            team: val?.id || "",
            teamName: val?.team || "",
            designation: "",
            designationName: ""
        }));

        if (formErrors.team) {
            setFormErrors(prev => ({ ...prev, team: undefined }));
        }

        setDesignations([]);
        if (val?.id) fetchDesignationsByTeamId(val.id);
    };

    const handleAddEmployee = async () => {
        setShowErrorMessage(false);
        const errors = {};

        if (!formData.firstName.trim()) errors.firstName = "First Name is required.";
        if (!formData.lastName.trim()) errors.lastName = "Last Name is required.";
       
        if (!formData.phoneNumber || formData.phoneNumber.trim() === "") {
            errors.phoneNumber = "Phone Number is required.";
        } else if (formData.phoneNumber.length < 10) {
            errors.phoneNumber = "Phone Number must be 10 digits.";
        } else if (!/^[6-9]\d{9}$/.test(formData.phoneNumber)) {
            errors.phoneNumber = "Must start with 6, 7, 8, or 9.";
        }
        if (!formData.dateOfBirth) {
            errors.dateOfBirth = "Date of Birth is required.";
        } else {
            const today = new Date();
            const birthDate = new Date(formData.dateOfBirth);
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }

            if (age < 18) {
                errors.dateOfBirth = "Employee must be at least 18 years old.";
            }
        }
        if (!formData.joiningDate) {
            errors.joiningDate = "Joining Date is required.";
        }
        if (!formData.department) errors.department = "Department is required.";
        if (!formData.team) errors.team = "Team is required.";
        if (isIdAvailable === false) {
            setSnackbar({ open: true, message: "Please use a unique Employee ID", severity: "error" });
            return;
        }
        if (!formData.location) errors.location = "Work Location is required.";

        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            setAddEmployeeErrorMessage("Please fill required fields.");
            setShowErrorMessage(true);
            return;
        }

        setLoading(true);
        try {
            const payload = {
                first_name: formData.firstName,
                last_name: formData.lastName,
                email: formData.email.toLowerCase(),
                phone_number: formData.phoneNumber,
                password: "1234",
                organization_id: orgId,
                department: formData.department,
                team: formData.team,
                designation: formData.designation,
                dob: formData.dateOfBirth,
                doj: formData.joiningDate,
                status: formData.status === "Active" ? "active" : "inactive",
                role_id: formData.roleId,
                report_manager_id: formData.reportingManager,
                location_id: formData.location,
                emp_id: formData.employeeId,
            };

            const response = await postData(API_ENDPOINTS.createUser, payload);
            if (response.status === 201 || response.status === "SUCCESS") {
                setSnackbar({ open: true, message: "Employee added!", severity: "success" });
                if (refreshData) refreshData();
                if (onClose) onClose();
            } else {
                setAddEmployeeErrorMessage(response.message || "Failed to add employee");
                setShowErrorMessage(true);
            }
        } catch (error) {
            const data = error.response?.data;
            let message = "Failed to add employee. Please try again.";
            const fieldErrors = {};

            if (Array.isArray(data?.errors)) {
                // zod schema validation errors: [{ path: ["field"], message: "..." }]
                data.errors.forEach((issue) => {
                    const feField = BACKEND_FIELD_MAP[issue.path?.[0]];
                    if (feField) fieldErrors[feField] = `Please check the ${feField} field`;
                });
                message = data.message || message;
            } else if (typeof data?.message === "string") {
                // business-rule errors, e.g. duplicate email/phone/emp_id, invalid role/manager
                message = data.message;
                const feField = BACKEND_FIELD_MAP[data.result?.field];
                if (feField) fieldErrors[feField] = data.message;
            }

            if (Object.keys(fieldErrors).length > 0) {
                setFormErrors(prev => ({ ...prev, ...fieldErrors }));
            }
            setSnackbar({ open: true, message, severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleEmployeeIdChange = (e) => {
        const value = e.target.value;
        updateField("employeeId", value);

        if (!value.trim()) {
            setIsIdAvailable(null);
            return;
        }

        const exists = existingEmployeeIds.includes(value.trim().toLowerCase());
        if (exists) {
            setIsIdAvailable(false);
            setSnackbar({
                open: true,
                message: `Employee ID "${value}" already exists!`,
                severity: "error"
            });
        } else {
            setIsIdAvailable(true);
        }
    };

    const capitalizeFirstLetter = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 3 }}>
                {showErrorMessage && <Alert severity="error">{addEmployeeErrorMessage}</Alert>}

                <Typography variant="h6" color="primary">Personal Information</Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                        <TextField
                            label="First Name" fullWidth size="small" required
                            value={formData.firstName}
                            onChange={(e) => {
                                const formattedValue = capitalizeFirstLetter(e.target.value);
                                updateField("firstName", formattedValue);
                            }}
                            error={!!formErrors.firstName}
                            helperText={formErrors.firstName}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <TextField
                            label="Last Name" fullWidth size="small" required
                            value={formData.lastName}
                            onChange={(e) => {
                                const formattedValue = capitalizeFirstLetter(e.target.value);
                                updateField("lastName", formattedValue);
                            }}
                            error={!!formErrors.lastName}
                            helperText={formErrors.lastName}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <TextField
                            label="Work Email"
                            fullWidth
                            size="small"
                            value={formData.email}
                            onChange={(e) => updateField("email", e.target.value)}
                            error={!!formErrors.email}
                            helperText={formErrors.email}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <TextField
                            label="Phone Number"
                            fullWidth
                            size="small"
                            required
                            inputProps={{ maxLength: 10 }}
                            value={formData.phoneNumber}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '');
                                if (val === '' || (/^[6-9]/.test(val) && val.length <= 10)) {
                                    updateField("phoneNumber", val);
                                }
                            }}
                            error={!!formErrors.phoneNumber}
                            helperText={formErrors.phoneNumber}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <DatePicker
                            label="Date of Birth"
                            value={formData.dateOfBirth}
                            onChange={(val) => updateField("dateOfBirth", val)}
                            maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 18))}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    required: true, // Adds the asterisk (*)
                                    error: !!formErrors.dateOfBirth,
                                    helperText: formErrors.dateOfBirth
                                }
                            }}
                            format="dd/MM/yyyy"
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <DatePicker
                            label="Joining Date"
                            value={formData.joiningDate}
                            onChange={(val) => updateField("joiningDate", val)}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    required: true,
                                    // Displaying the validation error
                                    error: !!formErrors.joiningDate,
                                    helperText: formErrors.joiningDate
                                }
                            }}
                            format="dd/MM/yyyy"
                        />
                    </Grid>
                </Grid>

                <Typography variant="h6" color="primary">Professional Information</Typography>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                        <Autocomplete
                            options={departments}
                            loading={loadingDepts}
                            getOptionLabel={(opt) => opt.department || ""}
                            value={departments.find(d => d.id === formData.department) || null}
                            onChange={handleDepartmentChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Department"
                                    size="small"
                                    required
                                    error={!!formErrors.department}
                                    helperText={formErrors.department}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingDepts ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                        <Autocomplete
                            options={teams}
                            loading={loadingTeams}
                            getOptionLabel={(opt) => opt.team || ""}
                            value={teams.find(t => t.id === formData.team) || null}
                            onChange={handleTeamChange}
                            disabled={!formData.department}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Team"
                                    size="small"
                                    required
                                    error={!!formErrors.team}
                                    helperText={formErrors.team}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingTeams ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <Autocomplete
                            options={designations}
                            loading={loadingDesignations}
                            getOptionLabel={(opt) => opt.designation || ""}
                            value={designations.find(d => d.id === formData.designation) || null}
                            onChange={(e, val) => updateField("designation", val?.id || "")}
                            disabled={!formData.team}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Designation"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingDesignations ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <Autocomplete
                            options={roles}
                            loading={loadingRoles}
                            getOptionLabel={(opt) => opt.name || ""}
                            value={roles.find(r => r.id === formData.roleId) || null}
                            onChange={(e, val) => updateField("roleId", val?.id || "")}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Role"
                                    size="small"
                                    required
                                    error={!!formErrors.roleId}
                                    helperText={formErrors.roleId}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingRoles ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <Autocomplete
                            options={teamLeadsAndManagers}
                            loading={loadingTeamLeadsAndManagers}
                            getOptionLabel={(opt) =>
                                `${opt.first_name} ${opt.last_name} (${opt.user_type})`
                            }
                            value={teamLeadsAndManagers.find(u => u.id === formData.reportingManager) || null}
                            onChange={(e, val) => updateField("reportingManager", val?.id || "")}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Reporting Manager"
                                    size="small"
                                    error={!!formErrors.reportingManager}
                                    helperText={formErrors.reportingManager}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingTeamLeadsAndManagers ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={formData.status}
                                label="Status"
                                onChange={(e) => updateField("status", e.target.value)}
                            >
                                <MenuItem value="inActive">Inactive</MenuItem>
                                <MenuItem value="Active">Active</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <TextField
                            label="Employee ID"
                            placeholder="e.g. EMP123"
                            fullWidth
                            size="small"
                            value={formData.employeeId}
                            onChange={handleEmployeeIdChange}
                            error={isIdAvailable === false || !!formErrors.employeeId}
                            helperText={
                                formErrors.employeeId ? (
                                    formErrors.employeeId
                                ) : isIdAvailable === false ? (
                                    "This ID is already taken"
                                ) : isIdAvailable === true ? (
                                    <span style={{ color: "green" }}>Employee ID is available</span>
                                ) : (
                                    ""
                                )
                            }
                            FormHelperTextProps={{
                                sx: { fontWeight: 500 }
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                        <Autocomplete
                            options={locations}
                            loading={loadingLocations}
                            getOptionLabel={(opt) => opt.location_name || ""}
                            value={locations.find(l => l.id === formData.location) || null}
                            onChange={(e, val) => updateField("location", val?.id || "")}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Work Location"
                                    size="small"
                                    required
                                    error={!!formErrors.location}
                                    helperText={formErrors.location}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingLocations ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>
                </Grid>


                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    {loading && <CircularProgress size={24} />}
                    <button
                        onClick={handleAddEmployee}
                        disabled={loading}
                        style={{
                            padding: '10px 24px',
                            backgroundColor: '#1976d2',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {loading ? "Saving..." : "Add Employee"}
                    </button>
                </Box>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </LocalizationProvider>
    );
}

export default AddEmployee;